/**
 * venuesSlice — single source of truth for the Venues & Spaces feature.
 *
 * Shared between the /venues-spaces page and the Settings → Venues section
 * so that adding/editing/deleting a venue or space reflects in both places.
 *
 * Reducers are pure — callers build the full object (including a stable id via
 * `localId` / `crypto.randomUUID`) before dispatching. No Date.now / Math.random
 * inside reducers.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Space } from "@/components/settings/venues/spaces/types";
import type { Venue } from "@/components/settings/venues/types";
import type { VenueStats } from "@/components/venues-spaces/venuesApiMappers";

export interface VenuesState {
  /** Venues from the operations API (hydrated by VenuesSpacesInitializer). */
  venues: Venue[];
  /** Spaces from the operations API (hydrated by VenuesSpacesInitializer). */
  spaces: Space[];
  /** KPI stats for the 4 stat cards (null until hydrated). */
  stats: VenueStats | null;
  /** True while the date-filter re-fetch of `stats` is in flight (card feedback). */
  statsLoading: boolean;
  /** True until the venues list is hydrated (set false by VenuesInitializer). */
  loading: boolean;
  /** True until the spaces list is hydrated (set false by SpacesInitializer). */
  spacesLoading: boolean;
  /** The id of the venue currently shown in the detail page (Figma 11.2). */
  venueDetailId: string | null;
  /** The space currently shown in the read-only details drawer (Figma 11.5). */
  spaceDetail: Space | null;
}

const initialState: VenuesState = {
  venues: [],
  spaces: [],
  stats: null,
  // Stats stream in their own Suspense boundary (slowest read) — start in the
  // loading state so the cards show it until VenueStatsInitializer hydrates.
  statsLoading: true,
  loading: true,
  spacesLoading: true,
  venueDetailId: null,
  spaceDetail: null,
};

const venuesSlice = createSlice({
  name: "venues",
  initialState,
  reducers: {
    // ── Hydration (from the operations API) ────────────────────────────────
    /** Replace the venues list (SSR hydration / refetch). */
    setVenues(state, action: PayloadAction<Venue[]>) {
      state.venues = action.payload;
    },
    /** Replace the spaces list (SSR hydration / refetch). */
    setSpaces(state, action: PayloadAction<Space[]>) {
      state.spaces = action.payload;
    },
    /** Replace the KPI stats (SSR hydration / refetch). */
    setVenueStats(state, action: PayloadAction<VenueStats>) {
      state.stats = action.payload;
    },
    /** Toggle the in-flight flag for the stats date-filter re-fetch. */
    setStatsLoading(state, action: PayloadAction<boolean>) {
      state.statsLoading = action.payload;
    },

    // ── Venues ────────────────────────────────────────────────────────────────
    setVenuesLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    addVenue(state, action: PayloadAction<Venue>) {
      state.venues.push(action.payload);
    },
    updateVenue(state, action: PayloadAction<Venue>) {
      const idx = state.venues.findIndex((v) => v.id === action.payload.id);
      if (idx !== -1) state.venues[idx] = action.payload;
    },
    deleteVenue(state, action: PayloadAction<string>) {
      state.venues = state.venues.filter((v) => v.id !== action.payload);
      // Also remove associated spaces
      state.spaces = state.spaces.filter((s) => s.venueId !== action.payload);
      if (state.venueDetailId === action.payload) {
        state.venueDetailId = null;
      }
    },

    // ── Spaces ────────────────────────────────────────────────────────────────
    setSpacesLoading(state, action: PayloadAction<boolean>) {
      state.spacesLoading = action.payload;
    },
    addSpaces(state, action: PayloadAction<Space[]>) {
      state.spaces.push(...action.payload);
    },
    updateSpace(state, action: PayloadAction<Space>) {
      const idx = state.spaces.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) state.spaces[idx] = action.payload;
    },
    deleteSpace(state, action: PayloadAction<string>) {
      state.spaces = state.spaces.filter((s) => s.id !== action.payload);
      if (state.spaceDetail?.id === action.payload) {
        state.spaceDetail = null;
      }
    },

    // ── Detail views ─────────────────────────────────────────────────────────
    openVenueDetail(state, action: PayloadAction<string>) {
      state.venueDetailId = action.payload;
    },
    closeVenueDetail(state) {
      state.venueDetailId = null;
    },
    openSpaceDetail(state, action: PayloadAction<Space>) {
      state.spaceDetail = action.payload;
    },
    closeSpaceDetail(state) {
      state.spaceDetail = null;
    },
  },
});

export const {
  setVenues,
  setSpaces,
  setVenueStats,
  setStatsLoading,
  setVenuesLoading,
  addVenue,
  updateVenue,
  deleteVenue,
  setSpacesLoading,
  addSpaces,
  updateSpace,
  deleteSpace,
  openVenueDetail,
  closeVenueDetail,
  openSpaceDetail,
  closeSpaceDetail,
} = venuesSlice.actions;

export default venuesSlice.reducer;
